import { Spinner } from '@Common/Spinner';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { useGetSubscription } from 'store/subscription/actions';

import { InvoiceStatusBadge } from '../UI/InvoiceStatusBadge';
import { Pagination } from '../UI/Pagination';

export const InvoicesTab = () => {
  const { data: subscription, isPending } = useGetSubscription();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const paginatedInvoices = subscription?.invoices.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  return (
    <div className="flex items-start flex-col w-full">
      <div className="text-lg font-semibold">
        <FormattedMessage id="invoices" />
      </div>
      {isPending ? (
        <Spinner color="bg-primary" />
      ) : (
        <>
          <div className="mt-3 w-full flex items-center">
            <div className="-my-2 overflow-x-auto w-full">
              <div className="inline-block min-w-full py-2 align-middle px-1">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                        >
                          <FormattedMessage id="amount" />
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          <FormattedMessage id="status" />
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          <FormattedMessage id="invoiceNumber" />
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          <FormattedMessage id="invoiceDate" />
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">Download</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {paginatedInvoices?.map((invoice) => (
                        <tr key={invoice.id}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            <FormattedNumber
                              style="currency"
                              currency="EUR"
                              value={invoice.amount_paid / 100}
                              maximumFractionDigits={0}
                            />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4">
                            <InvoiceStatusBadge status={invoice.status} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {invoice.number}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <FormattedDate
                              value={invoice.created * 1000}
                              year="numeric"
                              month="2-digit"
                              day="2-digit"
                            />
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 flex items-center justify-end">
                            <a
                              href={invoice.invoice_pdf}
                              download={true}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <div className="flex items-center">
                                <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                                Invoice PDF
                              </div>
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            totalItems={subscription?.invoices.length || 0}
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};
